import {
  Link,
  Button,
  IconButton,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { ChangeEvent, MouseEvent, useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import Page from 'src/components/Page';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { WorkflowManagementService } from './api/WorkflowManagementService';

interface Checklist {
  id: string;
  code: string;
  name: string;
  description: string;
}

const checklistInitialState: Checklist = {
  id: '',
  code: '',
  name: '',
  description: '',
};

export default function ChecklistList() {
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [checklistInForm, setChecklistInForm] = useState<Checklist>(checklistInitialState);
  const [editingChecklist, setEditingChecklist] = useState<Checklist | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedChecklist, setSelectedChecklist] = useState<Checklist | null>(null);

  const permissions = {
    isAllowedToAdd: checkAclValidation({ acls: [crsAcls.ADMIN.CHECKLIST.ADD] }),
    isAllowedToEdit: checkAclValidation({ acls: [crsAcls.ADMIN.CHECKLIST.EDIT] }),
  };

  const fetchChecklists = useCallback(async () => {
    try {
      setLoading(true);
      const response = await WorkflowManagementService.getAllChecklist();
      setChecklists(response);
    } catch (error) {
      console.error('Failed to fetch checklists', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChecklists();
  }, [fetchChecklists]);

  const handleOpen = () => {
    setEditingChecklist(null);
    setChecklistInForm(checklistInitialState);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setChecklistInForm(checklistInitialState);
    setEditingChecklist(null);
  };

  const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>, checklist: Checklist) => {
    setAnchorEl(event.currentTarget);
    setSelectedChecklist(checklist);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedChecklist(null);
  };

  const handleEditClick = () => {
    if (selectedChecklist) {
      setEditingChecklist(selectedChecklist);
      setChecklistInForm(selectedChecklist);
      setOpen(true);
    }
    handleMenuClose();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setChecklistInForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveChecklist = async () => {
    if (editingChecklist) {
      await handleUpdateChecklist();
    } else {
      await handleCreateChecklist();
    }
  };

  const handleCreateChecklist = async () => {
    try {
      await WorkflowManagementService.createChecklist(checklistInForm);
      handleClose();
      fetchChecklists();
    } catch (error) {
      console.error('Failed to create checklist', error);
    }
  };

  const handleUpdateChecklist = async () => {
    if (!editingChecklist) return;
    try {
      await WorkflowManagementService.updateChecklist(editingChecklist.code, checklistInForm);
      handleClose();
      fetchChecklists();
    } catch (error) {
      console.error('Failed to update checklist', error);
    }
  };

  return (
    <Page title="Checklists">
      <Container>
        <HeaderBreadcrumbs
          heading=""
          title="Checklist List"
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Checklists' }]}
          action={
            permissions.isAllowedToAdd && (
              <Button
                variant="contained"
                startIcon={<Iconify icon={'eva:plus-fill'} />}
                onClick={handleOpen}
              >
                New Checklist
              </Button>
            )
          }
        />
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {checklists.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Link component={RouterLink} to={`${PATH_DASHBOARD.crs.checklist}/${row.code}`}>
                        {row.code}
                      </Link>
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.description}</TableCell>
                    <TableCell align="right">
                      {permissions.isAllowedToEdit && (
                        <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                          <Iconify icon={'eva:more-vertical-fill'} width={20} height={20} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleEditClick}>
            <Iconify icon={'eva:edit-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            Edit
          </MenuItem>
        </Menu>
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>{editingChecklist ? 'Edit Checklist' : 'New Checklist'}</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <TextField
                name="code"
                label="Code"
                value={checklistInForm.code}
                onChange={handleInputChange}
                fullWidth
                disabled={!!editingChecklist}
              />
              <TextField
                name="name"
                label="Name"
                value={checklistInForm.name}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                name="description"
                label="Description"
                value={checklistInForm.description}
                onChange={handleInputChange}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button onClick={handleSaveChecklist} variant="contained">
              {editingChecklist ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}